import { forwardRef } from 'react';
import { useTranslation } from 'next-i18next';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '@components/common';
import { SidebarHeaderProps } from '@type/filters';

/**
 * @description The sidebar header component. It may contain a title, back button and delete button.
 * Used as a sticky section in the main view of the sidebar and in the form view of a sidebar group.
 * * **title** - the title.
 * * **hasBackButton** - whether it should display the back button or not.
 * * **hasDeleteButton** - whether it should display the delete button or not.
 * * **deleteButtonLabel** - a custom label for delete button.
 * * **backButtonClick** - the action that should happen on back button click.
 * * **deleteButtonClick** - the action that should happen on delete button click.
 */
export const SidebarHeader = forwardRef<HTMLDivElement, SidebarHeaderProps>((props, ref) => {
  // Translation
  const { t } = useTranslation('common');

  // Destructure props
  const {
    title,
    hasBackButton = false,
    hasDeleteButton = false,
    deleteButtonLabel = t('global.action.delete'),
    backButtonClick,
    deleteButtonClick,
  } = props;

  // Render component
  // ***********************************************
  return (
    <div ref={ref} className="sticky top-0 flex min-h-17 shrink-0 items-center border-b-6 border-b-surface-100 bg-surface px-4">
      { /* Back button */
        hasBackButton && (
          <Button
            styling="none"
            className="-ml-2 mr-1 shrink-0 rounded-full p-1.5 text-ink-medium transition hover:bg-surface-50 hover:text-ink"
            onClick={backButtonClick}
          >
            <ChevronLeftIcon className="size-6" />
          </Button>
        )
      }

      {/* Title */}
      <h4 className="grow text-xl font-bold">{title}</h4>

      { /* Delete button */
        hasDeleteButton && (
          <Button
            styling="text"
            size="sm"
            className="shrink-0 !font-bold"
            onClick={deleteButtonClick}
          >
            {deleteButtonLabel}
          </Button>
        )
      }
    </div>
  );
});
